import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

const LIMIT_LABELS = {
  DAY_SCAN_LIMIT: 'Сканирований в день',
  DAY_CHAT_LIMIT: 'Сообщений чата в день',
  MONTH_SCAN_LIMIT: 'Сканирований в месяц',
  MONTH_CHAT_LIMIT: 'Сообщений чата в месяц',
  MAX_IMAGE_SIZE: 'Макс. размер изображения',
  RATE_LIMIT: 'Лимит запросов'
};

const PROMPT_SECTIONS = [
  { key: 'scan', title: 'Промт сканирования' },
  { key: 'chatSystem', title: 'Системный промт чата' },
  { key: 'chatImage', title: 'Промт анализа фото в чате' }
];

const copyToClipboard = (text) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).catch((e) => console.error('Ошибка копирования:', e));
  }
};

const Row = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Box>{children}</Box>
  </Box>
);

/**
 * Вкладка «Конфигурация»: техданные конфигурации нейросети.
 */
const NeuroConfigPanel = ({ config }) => {
  if (!config) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={140} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  const chat = config.chat || {};
  const limits = config.limits || {};
  const pricing = config.pricing || {};
  const prompts = config.prompts || {};

  return (
    <Box>
      {/* Модель и подключение */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Модель и подключение</Typography>
          <Row label="Провайдер">
            <Typography variant="body2" fontWeight="bold">{config.provider || '—'}</Typography>
          </Row>
          <Row label="Модель">
            {config.model ? <Chip label={config.model} size="small" color="primary" variant="outlined" /> : '—'}
          </Row>
          <Row label="Base URL">
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{config.baseUrl || '—'}</Typography>
          </Row>
          <Row label="Прокси">
            <Chip
              label={config.proxyEnabled ? 'Включён' : 'Выключен'}
              size="small"
              color={config.proxyEnabled ? 'info' : 'default'}
              variant="outlined"
            />
          </Row>
          <Row label="API-ключ">
            <Chip
              label={config.apiKeySet ? 'Установлен' : 'Не установлен'}
              size="small"
              color={config.apiKeySet ? 'success' : 'warning'}
            />
          </Row>
          <Row label="maxTokens">
            <Typography variant="body2">{chat.maxTokens ?? '—'}</Typography>
          </Row>
          <Row label="temperature">
            <Typography variant="body2">{chat.temperature ?? '—'}</Typography>
          </Row>
          <Row label="historyMessages">
            <Typography variant="body2">{chat.historyMessages ?? '—'}</Typography>
          </Row>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Лимиты */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Лимиты</Typography>
              {Object.keys(limits).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Нет данных</Typography>
              ) : (
                Object.entries(limits).map(([key, value]) => (
                  <Row key={key} label={LIMIT_LABELS[key] || key}>
                    <Typography variant="body2" fontWeight="bold">
                      {typeof value === 'number' ? value.toLocaleString('ru-RU') : String(value)}
                    </Typography>
                  </Row>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Тарифы */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Тарифы (USD за 1M токенов)</Typography>
              {Object.keys(pricing).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Нет данных</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Модель</TableCell>
                      <TableCell align="right">Вход</TableCell>
                      <TableCell align="right">Выход</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(pricing).map(([model, price]) => (
                      <TableRow key={model}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{model}</TableCell>
                        <TableCell align="right">${price?.input ?? '—'}</TableCell>
                        <TableCell align="right">${price?.output ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Промты */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Промты</Typography>
          {PROMPT_SECTIONS.map(({ key, title }) => (
            <Accordion key={key} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">{title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {prompts[key] ? (
                  <>
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontSize: 12,
                        fontFamily: 'monospace',
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        p: 1.5,
                        m: 0,
                        mb: 1
                      }}
                    >
                      {prompts[key]}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copyToClipboard(prompts[key])}
                    >
                      Копировать
                    </Button>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">Промт не задан</Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

export default NeuroConfigPanel;
